import { Checkbox, Icon, Input, Tree } from 'antd';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import { FaChevronDown } from 'react-icons/all';
import PropTypes from 'prop-types';
import { useClickAway } from '../../../../../Hooks/useClickAway';

import useAsyncApi from '../../../../../services/async-api';
import { useQuery } from '@tanstack/react-query';

const { TreeNode } = Tree;
const { Search } = Input;
const TreeAreasComponent = (props) => {
   const [expandedKeys, setExpandedKeys] = useState([]);
   const [resultSearch, setResultSearch] = useState(null);
   const [searchValue, setSearchValue] = useState('');
   const [menuSelect, setMenuSelect] = useState(false);
   const [selectedCineBrasil, setSelectedCineBrasil] = useState([]);
   const treeRef = useRef(null);
   const menuRef = useRef(menuSelect);
   const responseApi = useRef([]);

   const api = useAsyncApi();

   const treeAreaRef = useClickAway((e) => {
      if (treeRef.current && !treeRef.current.contains(e.target) && menuRef.current) {
         handleOpenMenu(false);
      }
   });

   const getAreas = async () => {
      const data = await api.get(`sine/cine_brasil`);

      const groupedData = _(data)
         .groupBy('codigo_geral')
         .map((items, code) => ({
            area_geral: items[0].area_geral,
            code: code,
            children: _(items)
               .groupBy('area_especifica')
               .map((subItems, area_especifica) => ({
                  area_especifica,
                  children: subItems.map((a) => ({
                     title: a.rotulo_cine_brasil,
                     value: a.codigo,
                  })),
                  codigo_especifico: subItems[0].codigo_especifico,
               }))
               .sortBy('area_especifica')
               .value(),
         }))
         .sortBy('area_geral')
         .value();
      responseApi.current = data;

      return groupedData;
   };

   const removeAccents = (str) => {
      return str
         .normalize('NFD')
         .replace(/[\u0300-\u036f]/g, '')
         .toLowerCase();
   };

   const filterTree = (value) => {
      const searchValue = removeAccents(value.toLowerCase());
      const aux = areas || [];
      const result = aux
         .flatMap((item) =>
            item.children
               .map((group) => {
                  let matchGroup = removeAccents(group.area_especifica.toLowerCase()).includes(
                     searchValue
                  );
                  let hasMatchingSubItems = false;

                  const filteredSubItems = group.children.filter((subItem) => {
                     let matchSubItem = removeAccents(subItem.title.toLowerCase()).includes(
                        searchValue
                     );
                     if (matchSubItem) {
                        hasMatchingSubItems = true;
                     }
                     return matchSubItem;
                  });

                  if (matchGroup || hasMatchingSubItems) {
                     return {
                        codigo_geral: item.code,
                        area_especifica: group.area_especifica,
                        children: filteredSubItems,
                        codigo_especifico: group.codigo_especifico,
                     };
                  }
                  return null;
               })
               .filter(Boolean)
         )
         .filter((group) => group.children.length > 0);

      setResultSearch(result);
   };

   const toggleExpand = (key) => {
      setExpandedKeys((prevKeys) =>
         prevKeys.includes(key) ? prevKeys.filter((k) => k !== key) : [...prevKeys, key]
      );
   };

   const toggleRemoveBadge = (value) => {
      const aux = selectedCineBrasil;
      const filtered = aux.filter((k) => k.value !== value);
      setSelectedCineBrasil(filtered);
      props.onSelect(filtered);
   };

   const getExpandedKeys = (data, valuesToFind) => {
      const expandedKeys = [];

      const findKeys = (nodes) => {
         nodes.forEach((node) => {
            node.children.forEach((group) => {
               group.children.forEach((subItem) => {
                  if (valuesToFind.includes(subItem.value)) {
                     expandedKeys.push(node.code, node.code + group.codigo_especifico);
                  }
               });
            });
         });
      };

      findKeys(data);
      return expandedKeys;
   };
   const handleSelectCineBrasil = (item, tree) => {
      const { value, title } = item;
      let aux = selectedCineBrasil;

      if (props?.isAllowSelectMultipleOptions) {
         aux =
            aux.some((i) => i.value === item.value) ?
               aux.filter((k) => k.value !== value)
            :  [...aux, item];
         setSelectedCineBrasil(aux);
         props.onSelect(aux);

         const [geral, especifico, cine] = tree;
         toggleExpand(cine);

      } else {
         props.onSelect([item]);

         if (resultSearch !== null) {
            setExpandedKeys(tree);
         }
         setSearchValue(title);
         aux.some((i) => i.value === item.value) ?
            setSelectedCineBrasil([])
         :  setSelectedCineBrasil([item]);
      }
   };

   const handleOpenMenu = (isOpen) => {
      menuRef.current = isOpen;
      setMenuSelect(isOpen);
   };

   const { data: areas, isLoading } = useQuery({
      queryKey: ['cine_brasil_areas'],
      queryFn: getAreas,
      staleTime: Infinity,
   });

   const debounceSearch = useCallback(_.debounce(filterTree, 300), [areas]);

   useEffect(() => {
      if ('code' in props && !isLoading) {
         const code = props.code || [];
         if (code.length > 0) {
            setSelectedCineBrasil(code.map((i) => ({ title: i.label, value: i.code })));

            if (!props?.isAllowSelectMultipleOptions) {
               setSearchValue(code[0].label);
            }

            const keys = getExpandedKeys(
               areas,
               code.map((i) => i.code)
            );
            setExpandedKeys(keys);
         } else {
            setSelectedCineBrasil([]);
            setSearchValue('');
         }
      }
   }, [props.hasCode, isLoading]);

   useEffect(() => {
      if (!menuSelect) {
         if (treeRef) {
            const timeout = setTimeout(() => {
               if (treeRef) treeRef.current.style.display = 'none';
               setResultSearch(null);
            }, 300);

            return () => clearTimeout(timeout);
         }
      }
   }, [menuSelect]);

   return (
      <div ref={treeAreaRef} tabIndex={0} className={'tree-areas'}>
         <div className={props?.isAllowSelectMultipleOptions ? 'multiple' : ''}>
            {props?.isAllowSelectMultipleOptions && selectedCineBrasil.length > 0 && (
               <ul className={'selection-multiple'}>
                  {selectedCineBrasil.map((item) => (
                     <li className={''}>
                        {item.title}
                        <span
                           className={'choice-remove'}
                           onClick={() => toggleRemoveBadge(item.value)}
                        >
                           <Icon type={'close'} />
                        </span>
                     </li>
                  ))}
               </ul>
            )}
            <Search
               allowClear={searchValue?.length > 2}
               style={{ border: props.isAllowSelectMultipleOptions ? 'none' : '' }}
               placeholder={
                  api.loading ? 'Carregando aguarde...' : 'Clique para buscar e selecionar...'
               }
               className={props.isAllowSelectMultipleOptions ? 'border-none' : ''}
               value={searchValue}
               onFocus={() => {
                  if (!menuRef.current) {
                     treeRef.current.style.display = '';
                     treeRef.current.style.marginTop = '8px';
                     setTimeout(() => {
                        handleOpenMenu(true);
                     }, 50);
                  }
               }}
               loading={api.loading}
               disabled={api.loading}
               onSearch={(value, event) => {
                  if (event.target.value?.length === 0 && !props?.isAllowSelectMultipleOptions) {
                     props.onSelect([]);
                     setSelectedCineBrasil([]);
                     setSearchValue('');
                  }
               }}
               onChange={(e) => {
                  setSearchValue(e.target.value);
                  if (!e.target.value) {
                     setResultSearch(null);
                  } else {
                     debounceSearch(e.target.value);
                  }
               }}
            />
         </div>

         <div ref={treeRef} className={`tree ${menuSelect ? 'visible' : ''}`}>
            {resultSearch ?
               <div className={'results-filter-tree'}>
                  {/*{resultSearch.length > 0 && (*/}
                  {/*   <div className={'back-button'}>*/}
                  {/*      <button onClick={() => {*/}
                  {/*          setSearchValue('');*/}
                  {/*          setResultSearch(null);*/}
                  {/*          setExpandedKeys([])*/}
                  {/*      }} type={'button'} className={'btn-reseted btn-sm'}><FaArrowLeft size={12}/><small>Voltar</small></button>*/}
                  {/*   </div>*/}
                  {/*)}*/}
                  {resultSearch.length > 0 ?
                     resultSearch.map((especifico) => (
                        <section>
                           <h2>{especifico.area_especifica}</h2>
                           <div className={'areas-options'}>
                              {especifico.children.map((cine) => (
                                 <button
                                    className={'btn-reseted'}
                                    style={{ textAlign: 'start', fontSize: 14 }}
                                    onClick={() =>
                                       handleSelectCineBrasil(cine, [
                                          especifico.codigo_geral,
                                          especifico.codigo_especifico,
                                          cine.value,
                                       ])
                                    }
                                 >
                                    <Checkbox
                                       value={cine.value}
                                       style={{ marginRight: 5 }}
                                       checked={selectedCineBrasil.some(
                                          (i) => i.value === cine.value
                                       )}
                                    ></Checkbox>
                                    {cine.title}
                                 </button>
                              ))}
                           </div>
                        </section>
                     ))
                  :  <>
                        <h4>Nenhum resultado encontrado</h4>
                     </>
                  }
               </div>
            :  <Tree
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => {
                     setExpandedKeys(keys);
                  }}
                  switcherIcon={
                     <i>
                        <FaChevronDown />
                     </i>
                  }
                  showIcon={false}
                  onSelect={(selectedKeys, e) => {
                     toggleExpand(e.node.props.eventKey);
                  }}
               >
                  {areas?.map((item) => (
                     <TreeNode
                        icon={<Icon type="carry-out" />}
                        key={item.code}
                        title={<span>{item.area_geral}</span>}
                     >
                        {item.children.map((group, indexGroup) => (
                           <TreeNode
                              icon={<Icon type="carry-out" />}
                              key={item.code + group.codigo_especifico}
                              title={<span>{group.area_especifica}</span>}
                           >
                              {group.children.map((subItems) => (
                                 <TreeNode
                                    key={subItems.value}
                                    title={
                                       <span
                                          onClick={() =>
                                             handleSelectCineBrasil(subItems, [
                                                item.code,
                                                group.codigo_especifico,
                                                subItems.value,
                                             ])
                                          }
                                       >
                                          <Checkbox
                                             value={subItems.value}
                                             style={{ fontSize: 14 }}
                                             checked={selectedCineBrasil.some(
                                                (i) => i.value === subItems.value
                                             )}
                                          >
                                             {subItems.title}
                                          </Checkbox>
                                       </span>
                                    }
                                 ></TreeNode>
                              ))}
                           </TreeNode>
                        ))}
                     </TreeNode>
                  ))}
               </Tree>
            }
         </div>
      </div>
   );
};

TreeAreasComponent.propTypes = {
   isAllowSelectMultipleOptions: PropTypes.bool,
   enabled: PropTypes.bool,
   onSelect: PropTypes.func.isRequired,
   onUnmount: PropTypes.func,
   onSuccess: PropTypes.func,
   code: PropTypes.array,
   cachedData: PropTypes.shape({
      filteredData: PropTypes.array.isRequired,
      response: PropTypes.array.isRequired,
   }),
};
export default memo(TreeAreasComponent);
